import { useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { useEvent } from '../hooks/useEvents';
import { setupClientPassword } from '../services/emailService';
import { validatePassword } from '../utils/validators';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

function ClientSetupPage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { event, loading: eventLoading, error: eventError } = useEvent(eventId);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    setSubmitError('');

    const result = await setupClientPassword({ eventId, token, password });

    if (result.success) {
      // Store client session so gallery recognizes client mode immediately
      sessionStorage.setItem(`client_auth_${eventId}`, JSON.stringify({
        eventId,
        email: result.email || '',
        token: result.sessionToken || '',
        timestamp: Date.now()
      }));
      setIsSuccess(true);
    } else {
      setSubmitError(result.error || 'Failed to set up password. Please try again.');
    }

    setIsSubmitting(false);
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-slate">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (eventError || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-slate p-6">
        <ErrorMessage message={eventError || 'Event not found'} />
      </div>
    );
  }

  // Already set up
  if (event.clientSetupStatus === 'active') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
        }}
      >
        <div className="max-w-md w-full">
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Already Set Up</h1>
            <p className="text-neutral-grey-300 mb-8">
              Your gallery access for <span className="text-gold font-semibold">{event.eventName}</span> has already been configured.
            </p>
            <Link
              to="/client/portal"
              className="inline-block px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
            >
              Go to Client Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Missing or invalid token
  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
        }}
      >
        <div className="max-w-md w-full">
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-crimson" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Invalid Link</h1>
            <p className="text-neutral-grey-300">
              This setup link is missing or invalid. Please check your email for the correct link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
        }}
      >
        <div className="max-w-md w-full">
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">You're All Set!</h1>
            <p className="text-neutral-grey-300 mb-8">
              Your password has been created. You can now access and manage the gallery for <span className="text-gold font-semibold">{event.eventName}</span>.
            </p>
            <Link
              to={`/gallery/${eventId}`}
              className="inline-block px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
            >
              View Your Gallery
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Password setup form
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
      }}
    >
      <div className="max-w-md w-full">
        <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-gold" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Set Up Gallery Access
            </h1>
            <p className="text-neutral-grey-300">
              Create a password for <span className="text-gold font-semibold">{event.eventName}</span>
            </p>
          </div>

          {submitError && (
            <div className="bg-crimson/10 border border-crimson text-crimson px-4 py-3 rounded-lg mb-6">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-white font-medium mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormErrors(prev => ({ ...prev, password: null }));
                }}
                className={`w-full px-4 py-3 bg-white/10 border-2 ${
                  formErrors.password ? 'border-crimson' : 'border-white/20'
                } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                placeholder="Min. 6 characters"
                autoFocus
              />
              {formErrors.password && (
                <p className="mt-2 text-crimson text-sm">{formErrors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-white font-medium mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFormErrors(prev => ({ ...prev, confirmPassword: null }));
                }}
                className={`w-full px-4 py-3 bg-white/10 border-2 ${
                  formErrors.confirmPassword ? 'border-crimson' : 'border-white/20'
                } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                placeholder="Re-enter password"
              />
              {formErrors.confirmPassword && (
                <p className="mt-2 text-crimson text-sm">{formErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg shadow-gold/30"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="small" />
                  Setting Up...
                </span>
              ) : 'Set Up Access'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientSetupPage;
